import { convertHeic, isHeicFile } from "./heicConversion";

export interface CompressionOptions {
  maxWidth: number;
  maxHeight: number;
  quality: number;
  skipIfSmaller?: number; // Skip compression if file is already smaller than this (in KB)
}

/**
 * Compresses an image file using canvas
 * Automatically converts HEIC files to WebP/JPEG before compression
 */
export async function compressImage(file: File, options: CompressionOptions): Promise<File> {
  const { maxWidth, maxHeight, quality, skipIfSmaller } = options;

  // Convert HEIC to JPEG/PNG if needed (before compression)
  let fileToProcess = file;
  if (isHeicFile(file)) {
    try {
      fileToProcess = await convertHeic(file);
      console.log(`[imageCompression] Converted HEIC to ${fileToProcess.name} before compression`);
    } catch (error) {
      throw new Error(`Failed to convert HEIC file: ${error instanceof Error ? error.message : String(error)}`);
    }
  }

  // Skip compression if file is already small enough
  if (skipIfSmaller && fileToProcess.size <= skipIfSmaller * 1024) {
    console.log(
      `[imageCompression] Skipping compression - file size (${Math.round(
        fileToProcess.size / 1024,
      )}KB) is under limit (${skipIfSmaller}KB)`,
    );
    return fileToProcess;
  }

  return new Promise((resolve, reject) => {
    const canvas = document.createElement("canvas");
    const ctx = canvas.getContext("2d");
    const img = new Image();

    if (!ctx) {
      reject(new Error("Could not get canvas context"));
      return;
    }

    // Create object URL for the file
    const imageUrl = URL.createObjectURL(fileToProcess);

    img.onload = () => {
      // Clean up object URL immediately after image loads
      URL.revokeObjectURL(imageUrl);

      try {
        // Calculate new dimensions while maintaining aspect ratio
        const { width: newWidth, height: newHeight } = calculateDimensions(img.width, img.height, maxWidth, maxHeight);

        // Set canvas dimensions
        canvas.width = newWidth;
        canvas.height = newHeight;

        // Draw and compress
        ctx.drawImage(img, 0, 0, newWidth, newHeight);

        // Convert to blob
        canvas.toBlob(
          (blob) => {
            if (!blob) {
              reject(new Error("Failed to compress image"));
              return;
            }

            // Create new file with compressed data
            const compressedFile = new File([blob], fileToProcess.name, {
              type: blob.type,
              lastModified: Date.now(),
            });

            const originalSizeKB = Math.round(fileToProcess.size / 1024);
            const compressedSizeKB = Math.round(compressedFile.size / 1024);
            const savings = Math.round(((fileToProcess.size - compressedFile.size) / fileToProcess.size) * 100);

            console.log(
              `[imageCompression] Compressed ${fileToProcess.name}: ${originalSizeKB}KB → ${compressedSizeKB}KB (${savings}% reduction)`,
            );

            resolve(compressedFile);
          },
          fileToProcess.type.startsWith("image/png") ? "image/png" : "image/jpeg",
          quality,
        );
      } catch (error) {
        reject(error);
      }
    };

    img.onerror = () => {
      // Clean up object URL on error
      URL.revokeObjectURL(imageUrl);
      reject(new Error("Failed to load image"));
    };

    // Load the image
    img.src = imageUrl;
  });
}

/**
 * Calculate new dimensions while maintaining aspect ratio
 */
function calculateDimensions(
  originalWidth: number,
  originalHeight: number,
  maxWidth: number,
  maxHeight: number,
): { width: number; height: number } {
  if (originalWidth <= maxWidth && originalHeight <= maxHeight) {
    return { width: originalWidth, height: originalHeight };
  }

  const widthRatio = maxWidth / originalWidth;
  const heightRatio = maxHeight / originalHeight;
  const ratio = Math.min(widthRatio, heightRatio);

  return {
    width: Math.round(originalWidth * ratio),
    height: Math.round(originalHeight * ratio),
  };
}

/**
 * Validates if file is an image and under size limit
 */
export function validateImageFile(file: File, maxSizeMB: number = 10): string | null {
  // Check if it's an image (including HEIC/HEIF)
  const isImage = file.type.startsWith("image/");
  const isHeic = file.type === "image/heic" || file.type === "image/heif";
  const hasHeicExtension = file.name.toLowerCase().endsWith(".heic") || file.name.toLowerCase().endsWith(".heif");

  // Accept if it's an image type, or if it has HEIC extension (some browsers report empty MIME type for HEIC)
  if (!isImage && !isHeic && !hasHeicExtension) {
    return "Please select an image file";
  }

  // Check file size
  if (file.size >= maxSizeMB * 1024 * 1024) {
    return `File size must be less than ${maxSizeMB}MB`;
  }

  return null;
}
